// ---- Pure combat/AI sim (plan 13) -----------------------------------------
// The per-tick NPC step — chase+strike, roam, flee, respawn — extracted from
// index.ts so the game server AND the Model Studio's skills simulator tick the
// LITERAL same rules (no lookalike that drifts). The geometry was already pure
// (physics.ts); this module separates out the side effects: it mutates only the
// combatant records it's handed and EMITS EVENTS (`SimEvent`) for everything a
// hit/defeat/respawn *does* — applying damage to a player, loot/XP/quests,
// persistence dirty-flags — which stay with the caller (index.ts interprets
// them with damagePlayer/defeatNpc/npcDirty; the studio's combat driver maps
// them onto rig reactions).
//
// HARD RULE (one notch stricter than physics.ts): this module may not import
// index.ts or anything under src/server/, and may not touch document/window/
// Bun.*/process.env — the browser studio bundle imports it unchanged, and the
// server boots it headlessly. It imports TYPES plus its pure zero-import sim
// sibling `vertical.ts` (the gravity tunables — nothing else); all world
// geometry arrives through the `NavCtx` seam, so the server passes the
// real navWorld/terrain wrappers while the studio passes a trivial flat arena.
//
// Target SELECTION is a sim rule too (plan 14): `selectTarget` owns sticky-
// nearest + hysteresis + territory/leash, `assistScan` the pack-pull, `stanceOf`
// the faction table, `arcHits` the frontal cleave. The caller still owns
// BUILDING the candidate list (the server from its grid + engagedBy maps; the
// studio from its arena roster) and still applies every effect — stance tables
// and candidates are ARGUMENTS, never content imports.

import type { NpcSpec, CombatSpec, FleeSpec, TerritorySpec } from "../npc/define.ts";
import { V } from "./vertical.ts";
import { freshSlotState, slotGoal, slotHoldDist, SLOT_TUNING, type SlotAgent } from "./slots.ts";
export type { SlotAgent } from "./slots.ts";
export { touchSlotJoin, slotHoldDist, SLOT_TUNING, freshSlotState } from "./slots.ts";

// ---- Tuning shared by both sides -------------------------------------------
// ONE home for the combat economy numbers: the server (index.ts), the client
// HUD/prediction (src/combat.ts), and the studio sandbox all import these —
// a balance change lands everywhere or nowhere.
export const STA_REGEN = 1 / 1.5;  // stamina points/sec
export const PUNCH_STA_COST = 1;   // stamina per unarmed punch
export const RESPAWN_DELAY = 60;   // default seconds until a defeated NPC respawns (combat.respawn overrides per kind)
/** Default out-of-combat HP regen for fightable NPCs (points/sec). Overridden by
 *  combat.hpRegen; 0 on the kind disables. Only while they have no combat target
 *  (and aren't defeated) — leash arrival still snaps to full HP separately. */
export const NPC_HP_REGEN = 1;
export const PLAYER_PUNCH_CD = 0.5;// seconds between the player's unarmed punches (weapons override)
export const PLAYER_MAX_HP = 20;   // fresh-character hitpoints (state.ts mirrors this)
export const PLAYER_STA_MAX = 5;   // player stamina pool (state.ts mirrors this)
export const RUN_STA_DRAIN = 1;    // stamina/sec drained while sprinting
export const KO_DURATION = 2.6;    // seconds a player lies down before reviving to full
export const BLOCK_ARC = 120;      // frontal degrees a raised shield covers (plan 14)
export const BLOCK_STAGGER = 1.2;  // seconds staggered open when a blocked hit empties the pool
/** Hard chase cap as a multiple of territory.leash. After a hit re-pulls a
 *  leashing NPC past the soft leash, they can still fight until this distance
 *  from home (then peel). Stops infinite map-wide chases. */
export const LEASH_HARD_MULT = 1.5;

/** The minimal mutable state the sim reads/writes on either side of a fight.
 *  ServerNpc and the studio's combatants both satisfy it structurally. */
export interface Combatant {
  x: number; z: number; y: number; feetY: number; ry: number; bob: number;
  r: number;                     // collider radius, for self-aware obstacle sliding
  hp: number; maxHp: number; stamina: number; maxStamina: number;
  atkCd: number; atkSeq: number; // atkSeq++ per swing — clients/rigs fire the lunge once per increment
  // Vertical state (gravity plan): a drop past V.dropFreefall flips `air` in
  // advance(); verticalPass integrates vy and lands. Flat arenas (the parity
  // suite, the studio) never trigger it — surfaceAt ≡ feetY there.
  vy: number;                    // vertical velocity (units/sec; 0 while grounded)
  air: boolean;                  // ballistic (walked off a ledge / knocked back over one)
  peakY: number;                 // flight apex — fall-damage accounting on landing
  // Obstacle-aware pursuit (stuck detection + sidestep steering, see steerToward):
  blockedT: number;              // seconds of no forward progress toward the current goal
  detour: number;                // seconds left committed to a sidestep heading (0 = steering straight)
  detourX: number; detourZ: number; // the committed sidestep unit heading
  // Crowd-control timers (src/sim/status.ts) — 0 = free. Snare roots directed
  // locomotion; stun also locks strikes; slow multiplies locomotion speed.
  // Knockback residual is server-stepped outside the pure chase (kb* fields
  // live on ServerNpc / PlayerRuntime).
  snareT: number;
  stunT: number;
  slowT: number;
  slowMult: number;
  // Attack-slot ring (src/sim/slots.ts) — join-order surround for multi-engager melee.
  // Not wire/persisted. Cleared on target drop; rebalanced when peer set changes.
  slotTargetId: string | null;
  slotJoin: number;
  slotAngle: number;
  slotRingKey: string;
}

/** The NPC-side extension: roam waypoint, flee state, defeat/respawn timers.
 *  (index.ts's ServerNpc already carries every field; the studio allocates one.) */
export interface SimNpc extends Combatant {
  /** Identity for attack-slot peer lists (server always has this; studio sets it). */
  id?: string;
  tx: number; tz: number; pause: number;                        // roam
  fleeing: boolean; panic: number; fhx: number; fhz: number;    // flee (fhx/fhz = smoothed unit heading)
  defeated: boolean; respawnT: number;
  engaged: boolean;              // a qualifying target exists (drives the overhead bars)
  homeX: number; homeZ: number;  // territory anchor — the spawn point (re-homed on respawn)
  leashing: boolean;             // walking back home after a leash drop (no re-aggro until reset)
  /** Soft-leash arm: when true, walking past territory.leash peels the chase.
   *  Cleared by breakLeashOnHit so a re-pull past the soft leash can fight until
   *  the NPC re-enters the leash ring (re-arms) or hits the hard cap. */
  leashArmed: boolean;
}

/** What a flee creature scans for: any nearby player-shaped threat. */
export interface SimThreat {
  x: number; z: number;
  running?: boolean;             // widens the alert ring (runAlertMultiplier)
  down?: boolean;                // a knocked-out body is not a threat
}

/** Everything geometry-shaped the sim needs, abstracted so the studio can pass
 *  a FLAT arena while the server passes the real world (navWorld + terrain +
 *  structureKeepOuts + the interest grid). */
export interface NavCtx {
  /** navResolve: slide (x,z) out of world obstacles.  Studio: passthrough (± mutual collision). */
  resolve(x: number, z: number, feetY: number, r: number): { x: number; z: number };
  /** Standing height at (x,z) reachable from prevY.    Studio: 0. */
  surfaceAt(x: number, z: number, prevY: number): number;
  /** Keep a move out of the water.                     Studio: identity (no water). */
  clampToShore(fromX: number, fromZ: number, x: number, z: number): { x: number; z: number };
  /** Clamp a coordinate to the playable bound.         Studio: arena bound. */
  clamp(v: number): number;
  /** Is (x,z) underwater? (roam waypoint rejection)    Studio: false. */
  inWater(x: number, z: number): boolean;
  /** Structure keep-out circles (castle/cottages).     Studio: []. */
  keepOuts: { x: number; z: number; r: number }[];
  /** Player-shaped threats within r of (x,z) (flee).   Studio: [the player] if near. */
  threatsNear(x: number, z: number, r: number): SimThreat[];
}

/** The strike profile a chaser fights with. Built from an NPC's CombatSpec on
 *  the server; the studio's auto-battle player builds one from the equipped
 *  weapon's stats — both run the identical rules. */
export interface StrikeStats {
  range: number;                 // melee reach on the xz plane
  damage: number;                // HP removed per landed strike
  cooldown: number;              // seconds between strikes
  staminaCost: number;           // stamina per strike
  pursueSpeed: number;           // chase speed (units/sec)
}

/** What happened this tick that the CALLER must act on. The sim mutates only
 *  the attacker's own record — damage to the target, rewards, dirty-flags, and
 *  render reactions are all event-driven on the outside. */
export type SimEvent =
  | { kind: "swing" }            // attacker committed a strike (atkSeq already bumped) → render startAttack/lunge
  | { kind: "hit"; dmg: number; target?: string } // the strike connects → caller applies dmg. `target` names the
                                 // victim when the caller supplied an id (NPC-vs-NPC / cleave); absent = the implied target
  | { kind: "respawn" }          // a defeated NPC just stood back up → caller persists/rebroadcasts
  | { kind: "reset" }            // a leash-return just completed (healed at home) → caller clears engagement state
  | { kind: "shoot"; target?: string }; // an archer loosed at the target (gate already paid; atkSeq bumped) —
                                 // the caller rolls the distance band + flies the projectile (rng stays OUTSIDE the sim)

const NO_EVENTS: SimEvent[] = []; // shared empty result for the (common) uneventful tick

const rng = (a: number, b: number) => a + Math.random() * (b - a);

/** A fresh combatant record at full strength — the one place the SimNpc field
 *  set is enumerated (the test + the studio driver build from this; index.ts's
 *  ServerNpc spreads its extra server-only fields over the same shape). */
export function freshCombatant(at: { x: number; z: number }, r: number, hp: number, stamina: number): SimNpc {
  return {
    x: at.x, z: at.z, y: 0, feetY: 0, ry: 0, bob: 0, r,
    hp, maxHp: hp, stamina, maxStamina: stamina,
    atkCd: 0, atkSeq: 0,
    vy: 0, air: false, peakY: 0,
    blockedT: 0, detour: 0, detourX: 0, detourZ: 0,
    snareT: 0, stunT: 0, slowT: 0, slowMult: 1,
    ...freshSlotState(),
    tx: at.x, tz: at.z, pause: 0,
    fleeing: false, panic: 0, fhx: 0, fhz: 1,
    defeated: false, respawnT: 0,
    engaged: false,
    homeX: at.x, homeZ: at.z, leashing: false, leashArmed: true,
  };
}

/** A landed hit cancels a leash-return: the NPC can re-target the attacker under
 *  normal chase rules. Disarms the soft leash until they re-enter the leash
 *  ring (or hit the hard cap) so a re-pull just outside the line doesn't yo-yo. */
export function breakLeashOnHit(n: { leashing: boolean; leashArmed: boolean }): void {
  if (!n.leashing) return;
  n.leashing = false;
  n.leashArmed = false;
}

// Reused strike-profile scratch — npcStrike is on the 50Hz per-NPC hot path and
// CombatSpec is static content data, so rebuilding an object per tick is pure
// GC churn (same idiom as physics.ts's _cand). Consume synchronously; never
// retain the returned reference.
const _strike: StrikeStats = { range: 0, damage: 0, cooldown: 0, staminaCost: 0, pursueSpeed: 0 };

/** Strike profile for an NPC's combat block (the server-side chaser). Returns a
 *  shared scratch object — read it before the next npcStrike call. */
export function npcStrike(c: CombatSpec): StrikeStats {
  _strike.range = c.attackRange ?? 2.2;
  _strike.damage = c.attackDamage ?? 1;
  _strike.cooldown = c.attackCooldown ?? 1.5;
  _strike.staminaCost = PUNCH_STA_COST;
  _strike.pursueSpeed = c.pursueSpeed ?? 2.2;
  return _strike;
}

// ---- Factions & stances (plan 14) ------------------------------------------
// Pure over a PASSED table — the caller hydrates content/world/factions.json
// (server) or hands the arena's sandbox table (studio). `player` is just
// another faction id on the implicit player side.

export type Stance = "hostile" | "neutral" | "friendly";

/** Pair-keyed stance lookup (see makeStanceTable). */
export type StanceTable = Record<string, Stance>;

/** Order-independent key for a faction pair. */
export const stanceKey = (a: string, b: string): string => (a < b ? `${a}|${b}` : `${b}|${a}`);

/** Build the lookup from the content shape ({a, b, stance} rows). */
export function makeStanceTable(rows: { a: string; b: string; stance: Stance }[]): StanceTable {
  const t: StanceTable = {};
  for (const r of rows) t[stanceKey(r.a, r.b)] = r.stance;
  return t;
}

/** THE stance rule: same faction → friendly, unlisted pair (or either side
 *  unaligned) → neutral, otherwise the table's word. */
export function stanceOf(a: string | undefined, b: string | undefined, table: StanceTable): Stance {
  if (!a || !b) return "neutral";
  if (a === b) return "friendly";
  return table[stanceKey(a, b)] ?? "neutral";
}

// ---- Target selection (plan 14) ----------------------------------------------
// Moved INTO the sim so the server and the studio scenarios share identical
// aggro rules. The caller builds the candidate list (grid query + engagedBy on
// the server; the arena roster in the studio), already stance-filtered to
// hostiles, and applies the result.

/** One potential target, as the caller presents it. */
export interface SimCandidate {
  id: string;
  x: number; z: number;
  /** KO'd player / defeated NPC — never targetable. */
  down?: boolean;
  /** Already in combat with this NPC (hit it / adopted via assistScan). Unengaged
   *  candidates are only acquirable through a territory radius. Focus alone never
   *  marks a player engaged. */
  engaged?: boolean;
}

/** A challenger must be this much CLOSER (units) than the current target to
 *  steal aggro — the sticky-target hysteresis band. */
export const TARGET_SWAP_MARGIN = 2;

/** Sticky-nearest target selection with hysteresis, territory acquisition, and
 *  the leash drop. Deterministic (distance, ties by id — no rng).
 *
 *  Rules, in order:
 *  1. While leash-returning, nothing is targetable (until breakLeashOnHit).
 *  2. Territorial soft leash: if `leashArmed` and the NPC is past `leash` from
 *     home, drop and arm the walk home. Re-arm when inside the leash ring.
 *     Hard cap (`leash * LEASH_HARD_MULT`) always peels (even after a hit re-pull).
 *  3. Eligible = not down, within `exit` of the NPC, and either already engaged
 *     or (territorial) inside `territory.radius` of HOME.
 *  4. Keep the current target unless a challenger is closer by > `margin`.
 *
 *  Mutates `n.leashing` / `n.leashArmed` (selection-time facts); the walk home
 *  + heal live in stepNpc. */
export function selectTarget(
  n: SimNpc,
  territory: TerritorySpec | undefined,
  candidates: SimCandidate[],
  currentId: string | null,
  opts?: { margin?: number; exit?: number },
): SimCandidate | null {
  if (n.leashing) return null;
  if (territory) {
    const dHome = Math.hypot(n.x - n.homeX, n.z - n.homeZ);
    if (dHome < territory.leash) n.leashArmed = true; // re-arm soft peel inside the ring
    const hard = territory.leash * LEASH_HARD_MULT;
    if (dHome >= hard || (n.leashArmed && dHome >= territory.leash)) {
      n.leashing = true;
      n.leashArmed = true;
      return null;
    }
  }
  const margin = opts?.margin ?? TARGET_SWAP_MARGIN;
  const exit = opts?.exit ?? Infinity;
  let best: SimCandidate | null = null, bestD = Infinity;
  let current: SimCandidate | null = null, currentD = Infinity;
  for (const c of candidates) {
    if (c.down) continue;
    const d = Math.hypot(c.x - n.x, c.z - n.z);
    if (d > exit) continue;
    if (!c.engaged && !(territory && Math.hypot(c.x - n.homeX, c.z - n.homeZ) <= territory.radius)) continue;
    if (c.id === currentId) { current = c; currentD = d; }
    // Nearest wins; exact ties break by id (bestD only equals a finite d once
    // best is set) — rng-free like the rest of the melee path.
    if (d < bestD || (d === bestD && c.id < best!.id)) { best = c; bestD = d; }
  }
  if (current && best) {
    // Sticky: the first attacker holds aggro unless a challenger is closer by
    // more than the hysteresis margin.
    if (best.id !== current.id && bestD < currentD - margin) return best;
    return current;
  }
  return best;
}

/** The pack-pull: which foes should this NPC adopt because a nearby faction-mate
 *  is fighting them? `allies` = friendly-stance neighbors (caller-filtered),
 *  each with the id of who it's fighting / being attacked by (or null when
 *  idle). Returns deduped foe ids, sorted (deterministic); the caller merges
 *  them into its engagement state as `engaged` candidates. */
export function assistScan(
  n: { x: number; z: number },
  allies: { x: number; z: number; foeId: string | null }[],
  radius: number,
): string[] {
  const out = new Set<string>();
  for (const a of allies) {
    if (!a.foeId) continue;
    if (Math.hypot(a.x - n.x, a.z - n.z) <= radius) out.add(a.foeId);
  }
  return [...out].sort();
}

// ---- Frontal arc cleave (plan 14) --------------------------------------------

/** Cleave arc width (degrees) per attack style — thrust narrow, swing widest.
 *  ONE home (like the economy tunables above): the server's attack handler, the
 *  client's prediction, and the studio arena all read these. */
export const STYLE_ARC: Record<string, number> = {
  punch: 60,
  swing: 100,
  chop: 75,
  thrust: 40,
  draw: 0, // ranged — no melee arc
};

/** The effective light-attack arc for a weapon: its style's base width, widened
 *  for two-handers (THE anti-pack answer — 2H trades speed for coverage). */
export function weaponArc(style: string, grip: "one-hand" | "two-hand" = "one-hand"): number {
  const base = STYLE_ARC[style] ?? 60;
  return grip === "two-hand" ? base * 1.6 : base;
}

/** Absolute angle (radians) between a facing `ry` and the direction to (dx,dz),
 *  wrapped to [0, π] — the one copy of the frontal-arc angle test (arcHits +
 *  inFrontalArc share it). */
function facingDelta(ry: number, dx: number, dz: number): number {
  const dA = ((Math.atan2(dx, dz) - ry + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
  return Math.abs(dA);
}

/** Everyone the swing catches: within `range` (xz center distance, same rule as
 *  the single-target reach check) AND inside the frontal arc `arcDeg` wide,
 *  centered on the attacker's facing. Downed candidates never count. Returned
 *  SORTED BY ID so hit ordering is deterministic (the melee path stays rng-free
 *  for the parity tests). */
export function arcHits(
  attacker: { x: number; z: number; ry: number },
  arcDeg: number,
  range: number,
  candidates: SimCandidate[],
): SimCandidate[] {
  const half = (arcDeg * Math.PI) / 360;
  const out: SimCandidate[] = [];
  for (const c of candidates) {
    if (c.down) continue;
    const dx = c.x - attacker.x, dz = c.z - attacker.z;
    const d = Math.hypot(dx, dz);
    if (d > range) continue;
    // d≈0: standing inside the attacker — always struck.
    if (d > 1e-9 && facingDelta(attacker.ry, dx, dz) > half) continue;
    out.push(c);
  }
  return out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** Is `from` inside the defender's frontal arc? THE shield-block coverage rule
 *  (plan 14): the server's damagePlayer soaks only hits from inside BLOCK_ARC
 *  of the blocker's facing. Same angle math as arcHits. */
export function inFrontalArc(defender: { x: number; z: number; ry: number }, from: { x: number; z: number }, arcDeg: number): boolean {
  const dx = from.x - defender.x, dz = from.z - defender.z;
  if (Math.hypot(dx, dz) < 1e-9) return true;
  return facingDelta(defender.ry, dx, dz) <= (arcDeg * Math.PI) / 360;
}

/** Rotate the unit heading (hx,hz) toward the unit target (tx,tz) by at most
 *  `maxStep` radians, so a player circling a fleeing animal can't snap-flip its
 *  run direction in a single frame. Returns the new unit heading. */
export function steer(hx: number, hz: number, tx: number, tz: number, maxStep: number): [number, number] {
  const cur = Math.atan2(hx, hz), tgt = Math.atan2(tx, tz);
  let dA = ((tgt - cur + Math.PI * 3) % (Math.PI * 2)) - Math.PI; // shortest signed delta
  if (dA > maxStep) dA = maxStep; else if (dA < -maxStep) dA = -maxStep;
  const a = cur + dA;
  return [Math.sin(a), Math.cos(a)];
}

/** Is (x,z) a valid roam waypoint for a creature of collider radius `r`? Rejects
 *  water and the keep-out circles around the castle/cottages (padded by `r`). */
export function waypointOk(x: number, z: number, r: number, nav: NavCtx): boolean {
  if (nav.inWater(x, z)) return false;
  for (const k of nav.keepOuts)
    if (Math.hypot(x - k.x, z - k.z) < k.r + r) return false;
  return true;
}

/** Pick a fresh random respawn point for a roaming creature: a valid spot
 *  (dry land, clear of structure keep-outs) sampled from its roam rect, so a
 *  hunted animal reappears somewhere new rather than always at its authored
 *  spawn. Capped tries; falls back to the authored spawn if the rect is too
 *  constrained to land a valid sample. */
export function randomSpawn(n: SimNpc, spec: NpcSpec, nav: NavCtx): { x: number; z: number } {
  const r = spec.roam;
  if (r) {
    for (let t = 0; t < 24; t++) {
      const cx = rng(r.minX, r.maxX), cz = rng(r.minZ, r.maxZ);
      if (waypointOk(cx, cz, n.r, nav)) return { x: cx, z: cz };
    }
  }
  return { x: spec.x, z: spec.z };
}

/** How close a threat must be to spook a creature. A running player spooks it
 *  from farther out (`runAlertMultiplier`) — the "I can creep up but not charge" feel. */
function effectiveAlert(t: SimThreat, f: FleeSpec): number {
  return (f.alertRadius ?? 9) * (t.running ? (f.runAlertMultiplier ?? 1.7) : 1);
}

/** Scan threats for a flee creature: who (if anyone) is inside their own alert
 *  ring — the "deepest intruder" is the one to bolt away from — plus the nearest
 *  threat's distance (used for the calm/hysteresis check). A running player has a
 *  larger ring (`effectiveAlert`), so a charging player triggers it even if a
 *  closer player is ambling just outside their walking ring. */
export function scanThreat(n: SimNpc, f: FleeSpec, nav: NavCtx): { threat: SimThreat | null; nearestD: number } {
  let threat: SimThreat | null = null, deepest = 0, nearestD = Infinity;
  // Scoped: only threats within the widest ring that could matter (the boosted
  // alert radius or the calm-check safe radius). Anyone farther can neither
  // spook nor block calming, so nearestD = Infinity is equivalent.
  const scanR = Math.max((f.alertRadius ?? 9) * (f.runAlertMultiplier ?? 1.7), f.safeRadius ?? 16) + 1;
  for (const t of nav.threatsNear(n.x, n.z, scanR)) {
    if (t.down) continue; // a knocked-out body is not a threat (combat AI ignores them too)
    const d = Math.hypot(t.x - n.x, t.z - n.z);
    if (d < nearestD) nearestD = d;
    const intrusion = effectiveAlert(t, f) - d; // > 0 ⇒ inside this threat's alert ring
    if (intrusion > deepest) { deepest = intrusion; threat = t; }
  }
  return { threat, nearestD };
}

/** Spook a creature into fleeing directly away from `threat`: refresh its panic
 *  timer and turn its run heading away by at most `maxTurn` radians. Shared by the
 *  proximity trigger (a small per-frame turn) and a landed hit (a full snap-away). */
export function startFlee(n: SimNpc, f: FleeSpec, threat: { x: number; z: number }, maxTurn: number): void {
  n.fleeing = true;
  n.panic = Math.max(n.panic, f.panicMin ?? 1.2);
  let dx = n.x - threat.x, dz = n.z - threat.z;
  const dd = Math.hypot(dx, dz) || 1e-6; dx /= dd; dz /= dd;
  [n.fhx, n.fhz] = steer(n.fhx, n.fhz, dx, dz, maxTurn);
}

/** Locomotion mult from slow CC (1 when free). Snare/stun still gate via canMove. */
function speedMulOf(n: Combatant): number {
  if (n.slowT <= 0) return 1;
  const m = n.slowMult;
  return m > 0 && m < 1 ? m : 1;
}

/** Step a mover `speed*dt` along the unit direction (dx,dz)/d, sliding off world
 *  obstacles, then resolve its standing height + walk bob. Shared by chase + roam.
 *  Slow multiplies effective speed here so chase/roam/flee all slow for free. */
export function advance(n: Combatant, dx: number, dz: number, d: number, speed: number, dt: number, nav: NavCtx): void {
  const step = speed * speedMulOf(n) * dt;
  const cand = nav.resolve(n.x + (dx / d) * step, n.z + (dz / d) * step, n.feetY, n.r);
  const dry = nav.clampToShore(n.x, n.z, cand.x, cand.z); // can't wade into water
  const nx = nav.clamp(dry.x), nz = nav.clamp(dry.z); // stay inside the playable bound (a panicked bolt can't squeak past a corner)
  // Airborne cliff gate (gravity plan): a falling mover may not steer into a
  // column whose standing height is ABOVE its feet (it would end up inside the
  // terrain, below every landable surface). Grounded movers never hit this
  // branch, so the deterministic flat-arena trajectories are untouched.
  if (n.air && nav.surfaceAt(nx, nz, n.feetY) > n.feetY + 0.7) {
    // hold position — the fall continues straight down (verticalPass)
  } else {
    n.x = nx; n.z = nz;
  }
  const sy = nav.surfaceAt(n.x, n.z, n.feetY);
  // A real drop goes BALLISTIC (verticalPass owns the fall); anything shallower
  // keeps the classic per-tick snap. Flat arenas: sy ≡ feetY, never triggers.
  if (!n.air && n.feetY - sy > V.dropFreefall) {
    n.air = true; n.vy = 0; n.peakY = n.feetY;
  }
  if (!n.air) n.feetY = sy;
  n.bob += dt * 9;
  n.y = n.feetY + (n.air ? 0 : Math.abs(Math.sin(n.bob)) * 0.05);
}

/** Integrate an airborne NPC one tick (gravity plan): gravity on vy, land on
 *  the standable surface it crossed. Runs AFTER any horizontal displacement in
 *  the caller's step (chase/roam/flee — and any future knockback: a mover
 *  shoved past a ledge goes ballistic with no extra wiring, because advance()
 *  flips `air` on the resulting drop). Cheap early-out while grounded; a flat
 *  arena never sets `air`, so the parity suite can't see this pass. The caller
 *  applies fall damage from the returned landing. */
const NO_FALL: { landed: null | { fallDist: number } } = { landed: null };
export function verticalPass(n: Combatant, nav: NavCtx, dt: number): { landed: null | { fallDist: number } } {
  if (!n.air) return NO_FALL;
  const prevY = n.feetY;
  n.vy = Math.max(n.vy - V.gravity * dt, -V.maxFallVy);
  n.feetY += n.vy * dt;
  if (n.feetY > n.peakY) n.peakY = n.feetY;
  if (n.vy <= 0) {
    const land = nav.surfaceAt(n.x, n.z, prevY);
    // Land only on a surface we actually crossed this tick — a cliff face
    // (standing height far above prevY) is a wall, not a floor.
    if (n.feetY <= land && land <= prevY + 0.7) {
      const fallDist = Math.max(0, n.peakY - land);
      n.feetY = land;
      n.air = false; n.vy = 0; n.peakY = land;
      n.y = n.feetY + Math.abs(Math.sin(n.bob)) * 0.05;
      return { landed: { fallDist } };
    }
  }
  n.y = n.feetY;
  return NO_FALL;
}

// ---- Obstacle-aware pursuit: stuck detection + sidestep steering ------------
// Movement is otherwise pure "steer straight at the goal, slide off colliders"
// (advance). Against a wall or into a corner that sits between the mover and its
// goal the slide makes no FORWARD progress and the mover presses there forever
// (a roamer never reaches its waypoint to re-pick; a chaser never reaches the
// player). steerToward wraps advance with two behaviors keyed off one signal —
// how much of the intended step actually landed toward the goal:
//   • sidestep detour — after STUCK_TIME of no progress, probe left/right for a
//     clearer heading and commit to it for a short window, arcing around the
//     obstacle, then resume direct pursuit the moment the straight path reopens;
//   • give-up — blockedT keeps climbing while a sidestep isn't helping, so the
//     CALLER can read it (>= GIVEUP_TIME) and break the mover off (stepNpc walks
//     it home). Real progress toward the goal decays blockedT, so an open chase
//     (target simply running away) never trips either — the signal is "pressing
//     something," not "not catching up."
// Deterministic: the probe picks by clearance with a fixed tie-break, so the
// rng-free melee parity guard still holds. The studio's flat arena never blocks a
// mover, so blockedT stays 0 there and this whole path lies dormant.

export const STUCK_TIME = 0.45;     // seconds of no progress before a sidestep triggers
export const STUCK_PROGRESS = 0.35; // a step counts as progress if it lands ≥ this fraction toward the goal
export const DETOUR_TIME = 0.9;     // seconds committed to a sidestep once triggered
export const GIVEUP_TIME = 5;       // seconds of sustained no-progress before the caller breaks the mover off
export const ROAM_REPICK_TIME = 2;  // seconds stuck before a roamer abandons its waypoint for a new one
const PROBE_DIST = 1.5;             // how far ahead clearance is sampled when choosing a side
const PROBE_CLEAR = 0.7;            // clearance fraction that counts a heading as "open"

/** How open a unit heading (hx,hz) is from (n.x,n.z): probe two points along it
 *  and return the smaller surviving fraction after nav.resolve slides them out of
 *  colliders. 1 = fully clear, →0 = walled. Two samples so a thin wall the far
 *  probe would jump over is still caught. */
function clearance(n: Combatant, hx: number, hz: number, nav: NavCtx): number {
  let worst = 1;
  for (const f of [0.55, 1]) {
    const px = n.x + hx * PROBE_DIST * f, pz = n.z + hz * PROBE_DIST * f;
    const got = nav.resolve(px, pz, n.feetY, n.r);
    const moved = (got.x - n.x) * hx + (got.z - n.z) * hz; // progress along the heading
    const frac = moved / (PROBE_DIST * f);
    if (frac < worst) worst = frac;
  }
  return Math.max(0, worst);
}

// Candidate sidestep angles (radians) offset from the blocked goal heading,
// ordered by preference: veer slightly first, then harder, + side before − on a
// tie. Re-evaluated each detour cycle, so a long wall gets followed a step at a
// time toward whichever end is open.
const DETOUR_ANGLES = [Math.PI / 4, -Math.PI / 4, Math.PI / 2, -Math.PI / 2, (Math.PI * 3) / 4, (-Math.PI * 3) / 4];

/** Pick the clearest sidestep heading around an obstacle blocking the goal
 *  direction (ux,uz). Deterministic — the first angle to reach the best clearance
 *  wins (list order is the tie-break), so no rng enters the combat path. */
function pickDetour(n: Combatant, ux: number, uz: number, nav: NavCtx): [number, number] {
  const base = Math.atan2(ux, uz);
  let bestX = ux, bestZ = uz, best = -1;
  for (const off of DETOUR_ANGLES) {
    const a = base + off;
    const hx = Math.sin(a), hz = Math.cos(a);
    const c = clearance(n, hx, hz, nav);
    if (c > best) { best = c; bestX = hx; bestZ = hz; }
  }
  return [bestX, bestZ];
}

/** Steer a mover toward a goal, routing around obstacles. Straight at the goal
 *  when the path is clear; otherwise it commits to a sidestep (pickDetour) until
 *  the direct path reopens. Accrues `blockedT` while it can't make forward
 *  progress so the caller can give up. Shared by chase, roam, and leash-return —
 *  the non-blocked path is byte-identical to advancing straight at the goal, so
 *  the flat-arena parity tests are unaffected. */
export function steerToward(n: Combatant, gx: number, gz: number, speed: number, dt: number, nav: NavCtx): void {
  const dxG = gx - n.x, dzG = gz - n.z;
  const dG = Math.hypot(dxG, dzG) || 1e-6;
  const ux = dxG / dG, uz = dzG / dG;

  // Cut the sidestep short the instant the straight line to the goal opens up.
  if (n.detour > 0 && clearance(n, ux, uz, nav) > PROBE_CLEAR) n.detour = 0;

  let hx = ux, hz = uz;
  if (n.detour > 0) {
    n.detour -= dt;
    hx = n.detourX; hz = n.detourZ;
  } else if (n.blockedT >= STUCK_TIME) {
    [hx, hz] = pickDetour(n, ux, uz, nav);
    n.detour = DETOUR_TIME;
    n.detourX = hx; n.detourZ = hz;
  }

  const x0 = n.x, z0 = n.z;
  advance(n, hx, hz, 1, speed, dt, nav);
  n.ry = Math.atan2(hx, hz);

  // Progress is measured toward the GOAL, not along the (maybe sideways) heading,
  // so a sidestep that isn't actually getting us closer keeps blockedT climbing.
  // Use the same effective speed as advance (incl. slow) so a slowed chase doesn't
  // look "stuck" and give up early.
  const eff = speed * speedMulOf(n);
  const prog = (n.x - x0) * ux + (n.z - z0) * uz;
  if (prog < eff * dt * STUCK_PROGRESS) n.blockedT += dt;
  else n.blockedT = Math.max(0, n.blockedT - dt * 2); // real progress bleeds the timer down fast
}

/** Cool down the attack cooldown and regen stamina toward max. Shared by the NPC
 *  sim (`stepNpc`) and the server's per-player attack gate — same "cooldown ticks
 *  down, stamina regens" shape, just a different regen rate. */
export function tickAtkGate(g: { atkCd: number; stamina: number; maxStamina: number }, dt: number, regen: number): void {
  if (g.atkCd > 0) g.atkCd = Math.max(0, g.atkCd - dt);
  if (g.stamina < g.maxStamina) g.stamina = Math.min(g.maxStamina, g.stamina + regen * dt);
}

/** Resolve the out-of-combat HP regen rate (points/sec) for a kind. Omit →
 *  NPC_HP_REGEN; explicit 0 disables. Non-fightable specs yield 0. */
export function npcHpRegenRate(spec: NpcSpec): number {
  const c = spec.combat;
  if (!c) return 0;
  if (c.hpRegen === undefined) return NPC_HP_REGEN;
  return c.hpRegen >= 0 ? c.hpRegen : 0;
}

/** Regen HP toward max when out of combat. No-op at full HP or rate ≤ 0.
 *  Called from stepNpc (no target / mid-talk) and the AI-sleep tick path. */
export function tickNpcHpRegen(
  n: { hp: number; maxHp: number },
  rate: number,
  dt: number,
): void {
  if (rate <= 0 || n.hp >= n.maxHp) return;
  n.hp = Math.min(n.maxHp, n.hp + rate * dt);
}

/** Gate + spend cooldown/stamina for one swing. THE one copy of the spend —
 *  trySwingArc, Skill Sim specials, and any future shape-resolved attack share
 *  it so economy gates can't drift. */
export function spendSwing(a: Combatant, w: StrikeStats): boolean {
  if (a.stunT > 0) return false; // stunned: no act (status.ts canAct)
  if (a.atkCd > 0 || a.stamina < w.staminaCost) return false;
  a.stamina -= w.staminaCost;
  a.atkCd = w.cooldown;
  a.atkSeq++; // clients fire the lunge once per increment
  return true;
}

/** The cleave form (plan 14): ONE gate/spend, one `hit` per candidate the
 *  frontal arc catches (sorted by id — deterministic). A swing that catches
 *  nobody still spends (a whiff); null only when the gates are closed. */
export function trySwingArc(a: Combatant, w: StrikeStats, arcDeg: number, candidates: SimCandidate[]): SimEvent[] | null {
  if (!spendSwing(a, w)) return null;
  const events: SimEvent[] = [{ kind: "swing" }];
  for (const c of arcHits(a, arcDeg, w.range, candidates)) {
    events.push({ kind: "hit", dmg: w.damage, target: c.id });
  }
  return events;
}

/** Snap feet to the standable surface only while GROUNDED. Mid-loft / mid-fall
 *  (`air`) is owned by verticalPass — snare/stun must not cancel knockback height. */
function holdFeetIfGrounded(a: Combatant, nav: NavCtx): void {
  if (a.air) return;
  a.feetY = nav.surfaceAt(a.x, a.z, a.feetY);
  a.y = a.feetY;
}

/** The generalized chase-and-strike: close to `w.range`, then spend a strike
 *  when the cooldown + stamina gates open. Mutates only the attacker; the hit
 *  lands as an event the caller applies to the target (tagged with `t.id` when
 *  given — required once NPC-vs-NPC means the victim is no longer implied).
 *  The server runs this for every fighting NPC (its CombatSpec via `npcStrike`);
 *  the studio's auto-battle player runs it with the equipped weapon's stats —
 *  identical rules.
 *
 *  `peers` (optional): same-target melee engagers including self (SlotAgent
 *  records). When provided and self is in the list, chase steers at a surround
 *  **slot** instead of the target's feet (src/sim/slots.ts). Strike range and
 *  facing still use the real target. Omit peers to preserve classic direct chase
 *  (solo trajectory pins in combat.test.ts). */
export function chaseAndStrike(
  a: Combatant & { id?: string },
  t: { x: number; z: number; id?: string },
  w: StrikeStats,
  nav: NavCtx,
  dt: number,
  peers?: readonly SlotAgent[],
): SimEvent[] {
  const dx = t.x - a.x, dz = t.z - a.z;
  const d = Math.hypot(dx, dz) || 1e-6;
  // Stun: face only — no pursue, no strike (status.ts canAct).
  if (a.stunT > 0) {
    a.ry = Math.atan2(dx, dz);
    holdFeetIfGrounded(a, nav);
    return NO_EVENTS;
  }
  // Slot goal (when peers given): pursue the surround point, not target feet.
  // Strike range still uses the real target. While already in range we keep a
  // light pull toward the slot so engagers don't freeze mid-arc the moment they
  // clip the reach circle (that re-stacked packs before they spread).
  let slotGx: number | null = null, slotGz: number | null = null;
  if (peers && peers.length > 0 && a.id) {
    const self = peers.find((p) => p.id === a.id);
    if (self) {
      const goal = slotGoal(self, t, peers as SlotAgent[], slotHoldDist(w.range), nav);
      slotGx = goal.x; slotGz = goal.z;
    }
  }

  if (d > w.range) {
    // Snare: rooted — can still act when the target closes, but won't chase.
    if (a.snareT <= 0) {
      const gx = slotGx ?? t.x, gz = slotGz ?? t.z;
      steerToward(a, gx, gz, w.pursueSpeed, dt, nav); // routes around obstacles between us
    } else {
      holdFeetIfGrounded(a, nav);
    }
    a.ry = Math.atan2(dx, dz); // keep facing the REAL target even while sidestepping to a slot
    return NO_EVENTS;
  }

  // In strike range: optional ease onto the slot if we're still well off it.
  if (a.snareT <= 0 && slotGx != null && slotGz != null) {
    const gd = Math.hypot(slotGx - a.x, slotGz - a.z);
    if (gd > SLOT_TUNING.arrive) {
      steerToward(a, slotGx, slotGz, w.pursueSpeed, dt, nav);
    } else {
      a.blockedT = 0; a.detour = 0;
    }
  } else {
    a.blockedT = 0; a.detour = 0; // in reach — not stuck
  }
  a.ry = Math.atan2(dx, dz); // face the target for the strike
  if (!spendSwing(a, w)) return NO_EVENTS; // winded / on cooldown — nothing spent
  return [{ kind: "swing" }, t.id ? { kind: "hit", dmg: w.damage, target: t.id } : { kind: "hit", dmg: w.damage }];
}

/** Choose a fresh roam waypoint inside the rect, rejecting water and structure
 *  keep-outs (the rect is a single box and can't carve those holes). Capped tries
 *  so an over-constrained rect can never hang the tick; leaves the old target if
 *  none land. Shared by the arrival re-pick and the stuck re-pick. */
function repickWaypoint(n: SimNpc, r: NonNullable<NpcSpec["roam"]>, nav: NavCtx): void {
  for (let t = 0; t < 12; t++) {
    const cx = rng(r.minX, r.maxX), cz = rng(r.minZ, r.maxZ);
    if (waypointOk(cx, cz, n.r, nav)) { n.tx = cx; n.tz = cz; break; }
  }
}

/** Wander between random waypoints (mirrors npc/index.ts roam()). */
export function roamStep(n: SimNpc, spec: NpcSpec, nav: NavCtx, dt: number): void {
  const r = spec.roam!;
  if (n.pause > 0) {
    n.pause -= dt;
    n.feetY = nav.surfaceAt(n.x, n.z, n.feetY);
    n.y = n.feetY;
    return;
  }
  const dx = n.tx - n.x, dz = n.tz - n.z;
  const d = Math.hypot(dx, dz);
  if (d < 0.25) {
    n.pause = rng(r.pauseMin ?? 1, r.pauseMax ?? 3);
    n.blockedT = 0; n.detour = 0; // arrived — reset the stuck timer for the next leg
    repickWaypoint(n, r, nav);
    return;
  }
  steerToward(n, n.tx, n.tz, r.speed ?? 1.2, dt, nav); // routes around obstacles in the way
  // Couldn't reach this waypoint (walled off, and sidesteps didn't free it up) —
  // abandon it and wander somewhere else instead of pressing a wall forever.
  if (n.blockedT >= ROAM_REPICK_TIME) {
    n.blockedT = 0; n.detour = 0;
    repickWaypoint(n, r, nav);
  }
}

/** The pure half of a defeat: freeze the corpse state and arm the respawn timer.
 *  `delay` is seconds until respawn (default RESPAWN_DELAY); callers pass
 *  `spec.combat?.respawn ?? RESPAWN_DELAY` so kinds can override. Everything a
 *  defeat DOES (loot, XP, quests, engagement clears, persistence) stays with
 *  the caller (defeatNpc / the studio driver's topple). */
export function markDefeated(n: SimNpc, delay = RESPAWN_DELAY): void {
  n.defeated = true;
  n.hp = 0;
  n.engaged = false;
  n.fleeing = false; n.panic = 0; // a corpse isn't fleeing (clears the scamper pose on clients)
  n.leashing = false;             // …or walking home
  n.leashArmed = true;
  n.blockedT = 0; n.detour = 0;   // …or stuck on anything
  n.snareT = 0; n.stunT = 0; n.slowT = 0; n.slowMult = 1; // corpse has no CC
  n.slotTargetId = null; n.slotJoin = 0; n.slotAngle = 0; n.slotRingKey = ""; // leave the attack ring
  n.respawnT = delay;
}

/** Stand a defeated NPC back up at full strength. Wildlife respawn at a random
 *  valid spot in their roam range (not on water or a structure); other NPCs
 *  return to their authored spawn. */
export function respawnNpc(n: SimNpc, spec: NpcSpec, nav: NavCtx): void {
  n.defeated = false;
  n.hp = n.maxHp;
  n.stamina = n.maxStamina;
  n.atkCd = 0;
  const sp = spec.flee ? randomSpawn(n, spec, nav) : { x: spec.x, z: spec.z };
  n.x = sp.x; n.z = sp.z;
  n.ry = spec.facing ?? 0;
  n.feetY = nav.surfaceAt(n.x, n.z, spec.y);
  n.y = n.feetY;
  n.tx = n.x; n.tz = n.z; n.pause = 0; n.bob = 0;
  n.fleeing = false; n.panic = 0; n.fhx = 0; n.fhz = 1;
  n.blockedT = 0; n.detour = 0;
  n.snareT = 0; n.stunT = 0; n.slowT = 0; n.slowMult = 1;
  n.slotTargetId = null; n.slotJoin = 0; n.slotAngle = 0; n.slotRingKey = "";
  n.homeX = sp.x; n.homeZ = sp.z; n.leashing = false; n.leashArmed = true; // territory re-anchors
}

const RESPAWN_EVENTS: SimEvent[] = [{ kind: "respawn" }];
const RESET_EVENTS: SimEvent[] = [{ kind: "reset" }];

/** Advance one NPC by dt: respawn timer, regen/cooldown, talk-freeze, then
 *  leash-return, flee, fight, or roam. Pure — the caller picks the `target`
 *  (selectTarget over its candidate list; tag it with `id` so hits name their
 *  victim), resolves the `talker` to a position (or null), and interprets the
 *  returned events (`hit` → damage the named target, `respawn` → persist,
 *  `reset` → clear engagement state after a leash return). */
export function stepNpc(
  n: SimNpc,
  spec: NpcSpec,
  target: { x: number; z: number; id?: string } | null,
  nav: NavCtx,
  dt: number,
  talker: { x: number; z: number } | null = null,
  /** Same-target engagers for melee attack slots (see chaseAndStrike). */
  peers?: readonly SlotAgent[],
): SimEvent[] {
  if (n.defeated) {
    n.respawnT -= dt;
    if (n.respawnT <= 0) { respawnNpc(n, spec, nav); return RESPAWN_EVENTS; }
    return NO_EVENTS;
  }
  tickAtkGate(n, dt, STA_REGEN);

  // Out-of-combat HP regen: no fight target (and talk freezes combat). Immediate
  // once the target drops; rate from combat.hpRegen ?? NPC_HP_REGEN (0 = off).
  // In combat (has target, not talking) we skip so chip-damage mid-fight sticks.
  const inCombat = !talker && !!target;
  if (!inCombat) tickNpcHpRegen(n, npcHpRegenRate(spec), dt);

  // In a conversation: stand still and face the talker (the caller already
  // validated the lock — range/disconnect drops happen on its side).
  if (talker) {
    n.ry = Math.atan2(talker.x - n.x, talker.z - n.z);
    n.engaged = false;
    n.feetY = nav.surfaceAt(n.x, n.z, n.feetY);
    n.y = n.feetY;
    return NO_EVENTS;
  }

  const c = spec.combat;
  n.engaged = !!target;

  // Full stun freeze: face the target (if any); no directed move/act. Knockback
  // residual is stepped by the server wrapper BEFORE this call. While airborne
  // (loft/fall) do NOT snap feet — verticalPass owns height until land.
  if (n.stunT > 0) {
    if (target) n.ry = Math.atan2(target.x - n.x, target.z - n.z);
    holdFeetIfGrounded(n, nav);
    return NO_EVENTS;
  }

  // Returning home: either a territory leash-drop (selectTarget armed n.leashing),
  // an off-patch territorial NPC with no target, or a stuck give-up (the chase
  // branch below arms n.leashing when it can't reach its target). Walk home
  // (regen already ticked above), heal to full on arrival, and emit `reset` so
  // the caller clears engagement. Not gated on `territory` — that's how a
  // non-territorial chaser breaks off something it genuinely can't path to,
  // instead of pressing a wall forever.
  const terr = spec.territory;
  if (terr && !target && !n.leashing && Math.hypot(n.x - n.homeX, n.z - n.homeZ) > terr.radius) {
    n.leashing = true;
  }
  if (n.leashing) {
    const dx = n.homeX - n.x, dz = n.homeZ - n.z;
    const d = Math.hypot(dx, dz);
    if (d < 0.5) {
      n.leashing = false;
      n.leashArmed = true;
      n.hp = n.maxHp; n.stamina = n.maxStamina; n.atkCd = 0; // the reset heal
      n.tx = n.x; n.tz = n.z; n.pause = 0;                  // settle here, roam resumes
      n.blockedT = 0; n.detour = 0;
      return RESET_EVENTS;
    }
    // Snare roots leash-walk too (timer is short; fight resumes after).
    if (n.snareT <= 0) {
      steerToward(n, n.homeX, n.homeZ, c?.pursueSpeed ?? 2.2, dt, nav); // route around obstacles heading home too
    } else {
      holdFeetIfGrounded(n, nav);
    }
    return NO_EVENTS;
  }

  // Wildlife (has a `flee` block): scamper supersedes the combat chase AI. Prey is
  // huntable (the combat block gives it HP + defeat/respawn + the overhead bars) but
  // never pursues or punches — it only bolts away and then calms back into roaming.
  const f = spec.flee;
  if (f) {
    const { threat, nearestD } = scanThreat(n, f, nav);
    if (threat) {
      startFlee(n, f, threat, (f.turnRate ?? 6) * dt); // smooth per-frame turn away
    }
    if (n.fleeing) {
      n.panic -= dt;
      // Calm only when committed-bolt time is up AND the nearest player is past the
      // (larger) safe radius — the gap between alert and safe is the anti-flicker band.
      const calm = n.panic <= 0 && nearestD > (f.safeRadius ?? 16);
      if (calm) {
        n.fleeing = false;
        n.tx = n.x; n.tz = n.z; n.pause = rng(0.3, 0.8); // settle here, don't sprint to a stale waypoint
      } else if (n.snareT <= 0) {
        advance(n, n.fhx, n.fhz, 1, f.fleeSpeed ?? 8.4, dt, nav); // bolt along the smoothed heading
        n.ry = Math.atan2(n.fhx, n.fhz);
        return NO_EVENTS;
      } else {
        // Snared mid-flee: hold directed bolt, keep panic ticking; loft intact.
        holdFeetIfGrounded(n, nav);
        return NO_EVENTS;
      }
    }
    if (spec.roam && n.snareT <= 0) roamStep(n, spec, nav, dt);
    else holdFeetIfGrounded(n, nav);
    return NO_EVENTS;
  }

  if (target && c) {
    // Archer (combat.ranged, plan 14): hold range and shoot. Inside the fire
    // band but outside melee reach it STANDS and looses on the normal attack
    // gate (no kiting — closed on, it falls back to melee; past the band, it
    // closes in). The shot itself is a `shoot` event: the caller rolls the
    // distance band + flies the projectile, so the melee path stays rng-free.
    const r = c.ranged;
    if (r) {
      const w = npcStrike(c);
      const d = Math.hypot(target.x - n.x, target.z - n.z);
      const fireMax = r.bands[r.bands.length - 1]!.max;
      if (d > w.range && d <= fireMax) {
        n.ry = Math.atan2(target.x - n.x, target.z - n.z);
        if (spendSwing(n, w)) {
          return [target.id ? { kind: "shoot", target: target.id } : { kind: "shoot" }];
        }
        return NO_EVENTS;
      }
      // d > fireMax falls through to chaseAndStrike, which walks it into the
      // band (it strikes only inside melee reach, so no early swing fires).
    }
    // Melee chase (and archer closing into band) use slots when peers are given.
    // Archer stand-and-shoot above does not — slots are melee-pursuit only.
    const evs = chaseAndStrike(n, target, npcStrike(c), nav, dt, peers);
    if (n.blockedT >= GIVEUP_TIME) {
      // Sustained no-progress: the target is walled off and the sidesteps aren't
      // getting there. Break off — the leash block above walks it home and emits
      // `reset` next tick (selectTarget returns null while leashing).
      n.leashing = true;
      n.blockedT = 0; n.detour = 0;
    }
    return evs;
  }
  if (spec.roam && n.snareT <= 0) roamStep(n, spec, nav, dt);
  else holdFeetIfGrounded(n, nav);
  return NO_EVENTS;
}
